// src/components/Tableros/Campo/VerticalScoreboard.js
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import VerticalEventTable from '../Campo/Tablas/VerticalEventTable';

/**
 * Componente para mostrar el tablero vertical en una ventana independiente
 * Carga la configuración desde localStorage y la pasa al componente de visualización
 */
const VerticalScoreboard = () => {
  const [searchParams] = useSearchParams();
  const configId = searchParams.get('id');
  
  // Estados para almacenar configuración y datos
  const [config, setConfig] = useState(null);
  const [displayRows, setDisplayRows] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Actualizar dimensiones cuando cambia el tamaño de la ventana
  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cargar la configuración desde localStorage
  useEffect(() => {
    if (!configId) {
      setError('No se proporcionó ID de configuración');
      setLoading(false);
      return;
    }

    const loadConfig = () => {
      try {
        const savedConfig = localStorage.getItem(`config-${configId}`);
        console.log('Configuración cargada:', savedConfig); // Debug
        
        if (savedConfig) {
          const parsedConfig = JSON.parse(savedConfig);
          console.log('Config parseada:', parsedConfig); // Debug completo
          setConfig(parsedConfig);
          
          // Usar el número de participantes si existe, o el displayRows configurado
          const participantCount = parsedConfig.eventData?.participants?.length || 0;
          const configuredRows = parsedConfig.settings?.displayRows || 8;
          const rows = participantCount > 0 ? participantCount : configuredRows;
          
          console.log('Participantes:', participantCount, 'Filas configuradas:', configuredRows, 'Filas a mostrar:', rows);
          setDisplayRows(rows);
        } else {
          setError(`No se encontró configuración para el ID: ${configId}`);
        }
      } catch (err) {
        console.error('Error al cargar la configuración:', err);
        setError(`Error al cargar la configuración: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();

    // Escuchar mensajes para actualizar la configuración
    const handleMessage = (event) => {
      // Verificar origen por seguridad
      if (event.origin !== window.location.origin) return;
      
      console.log('Mensaje recibido en VerticalScoreboard:', event.data);
      
      if (event.data) {
        if (event.data.type === 'CONFIG_UPDATE' || event.data.type === 'INITIAL_CONFIG') {
          console.log('Recibida actualización de configuración:', event.data.type);
          
          if (event.data.config) {
            const newConfig = event.data.config;
            console.log('Nueva configuración recibida:', newConfig);
            console.log('EventData en nueva config:', newConfig.eventData);
            
            setConfig(newConfig);
            
            // Actualizar localStorage
            localStorage.setItem(`config-${configId}`, JSON.stringify(newConfig));
            
            // Actualizar displayRows
            const participantCount = newConfig.eventData?.participants?.length || 0;
            const configuredRows = newConfig.settings?.displayRows || 8;
            const rows = participantCount > 0 ? participantCount : configuredRows;
            setDisplayRows(rows);
            
            console.log('Configuración actualizada exitosamente');
          }
        } else if (event.data.type === 'updateConfig') {
          // Mantener compatibilidad con el mensaje anterior
          console.log('Recibido mensaje updateConfig (legacy)');
          loadConfig();
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    // Notificar que el tablero está listo
    if (window.opener) {
      window.opener.postMessage({ type: 'SCOREBOARD_READY', configId }, '*');
    }
    
    // Configurar el tamaño del documento
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';
    document.body.style.backgroundColor = '#1a1a1a';
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [configId]);
  

  // Si está cargando, mostrar un indicador
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        width: '100vw',
        backgroundColor: '#1a1a1a',
        color: 'white'
      }}>
        <CircularProgress color="primary" sx={{ mr: 2 }} />
        <Typography>Cargando tablero vertical...</Typography>
      </Box>
    );
  }

  // Si hay un error, mostrarlo
  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        width: '100vw',
        backgroundColor: '#1a1a1a',
        color: 'white',
        flexDirection: 'column',
        p: 3
      }}>
        <Typography variant="h6" color="error" gutterBottom>Error</Typography>
        <Typography>{error}</Typography>
      </Box>
    );
  }

  // Si no hay configuración o no hay eventData, mostrar mensaje
  if (!config || !config.eventData) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        width: '100vw',
        backgroundColor: '#1a1a1a',
        color: 'white',
        flexDirection: 'column',
        gap: 2
      }}>
        <Typography variant="h6">Esperando datos del evento...</Typography>
        <Typography variant="body2" color="textSecondary">
          La configuración se ha cargado pero los datos del evento aún no están disponibles.
        </Typography>
        {config && (
          <Typography variant="caption" color="textSecondary">
            Config ID: {configId}
          </Typography>
        )}
      </Box>
    );
  }

  // Extraer las configuraciones
  const { settings, eventData } = config;
  
  // Log para depuración
  console.log('Configuración cargada para VerticalScoreboard:', {
    settings,
    eventData,
    displayRows,
    participants: eventData?.participants
  });

 
  
  // Usar el tamaño de la ventana como predeterminado si está disponible
  const tableWidth = windowDimensions.width;
  const tableHeight = windowDimensions.height;

  // Procesar los datos de los atletas desde eventData
  const processAthletes = () => {
    if (!eventData?.participants || eventData.participants.length === 0) {
      console.warn('No hay participantes en eventData, generando datos de ejemplo');
      // Generar datos de ejemplo solo si no hay datos reales
      return Array.from({ length: displayRows }).map((_, index) => ({
        position: `${index + 1}`,
        name: `Atleta Ejemplo ${index + 1}`,
        team: `Equipo ${index + 1}`,
        height: (2.20 - index * 0.05).toFixed(2),
        attempts: [
          index % 3 === 0 ? 'X' : (index % 3 === 1 ? 'P' : 'O'),
          index % 3 === 1 ? 'X' : (index % 3 === 2 ? 'P' : 'O'),
          index % 3 === 2 ? 'X' : (index % 3 === 0 ? 'P' : 'O')
        ],
        lane: `${index + 1}`
      }));
    }


    console.log('Procesando participantes reales:', eventData.participants);

    // Procesar participantes reales
    return eventData.participants
      .sort((a, b) => {
        // Ordenar por posición si existe
        const posA = parseInt(a.place) || 999;
        const posB = parseInt(b.place) || 999;
        if (posA !== posB) return posA - posB;
        
        // Si las posiciones son iguales, ordenar por lane
        return (parseInt(a.lane) || 0) - (parseInt(b.lane) || 0);
      })
      .map((participant, index) => {
        console.log(`Procesando participante ${index}:`, participant);
        
        // Obtener la mejor marca de los intentos
        let bestHeight = '-';
        let formattedAttempts = ['-', '-', '-']; // Valor por defecto
        
        if (participant.attempts && participant.attempts.length > 0) {
          // Procesar intentos existentes
          const validAttempts = participant.attempts
            .filter(att => att && att.mark && !isNaN(parseFloat(att.mark)))
            .map(att => parseFloat(att.mark));
          
          if (validAttempts.length > 0) {
            bestHeight = Math.max(...validAttempts).toFixed(2);
          }
          
          // Formatear los intentos
          formattedAttempts = participant.attempts.map((attempt) => {
            if (!attempt) return '-';
            if (attempt.special === 'X' || attempt.result === 'X') return 'X';
            if (attempt.special === 'P' || attempt.result === 'P') return 'P';
            if (attempt.special === 'O' || attempt.result === 'O') return 'O';
            if (attempt.mark && !isNaN(parseFloat(attempt.mark))) return 'O';
            return '-';
          });
          
          // Asegurar que siempre tengamos al menos 3 intentos mostrados
          while (formattedAttempts.length < 3) {
            formattedAttempts.push('-');
          }
        }

        const processedAthlete = {
          position: participant.place === '-' || !participant.place ? `${index + 1}` : participant.place,
          name: participant.fullName || participant.name || 'Sin nombre',
          team: participant.affiliation || participant.team || '',
          height: bestHeight,
          attempts: formattedAttempts,
          lane: participant.lane?.toString() || `${index + 1}`,
          // Datos adicionales que podrían ser útiles
          idNumber: participant.idNumber,
          special: participant.special || '-'
        };
        
        console.log(`Atleta procesado:`, processedAthlete);
        return processedAthlete;
      })
      .slice(0, displayRows); // Limitar al número de filas a mostrar
  };

  // Procesar lista de atletas
  const athletesList = processAthletes();
  
  console.log('Lista final de atletas procesados:', athletesList);
  console.log('Número de atletas a mostrar:', athletesList.length);

  // Componente simple para verificar datos
  const SimpleVerticalDisplay = ({ eventData, athletesList, settings }) => {
    return (
      <Box sx={{ 
        width: '100%', 
        height: '100%', 
        backgroundColor: settings?.tableBackgroundColor || '#1a1a1a',
        color: 'white',
        p: 4
      }}>
        {/* Header */}
        <Box sx={{ 
          backgroundColor: settings?.headerBackgroundColor || '#2c0369',
          p: 3,
          mb: 3,
          textAlign: 'center'
        }}>
          <Typography 
            variant="h2" 
            sx={{ 
              color: settings?.headerFontColor || '#ffffff',
              fontSize: settings?.headerFontSize || 48,
              fontFamily: settings?.headerFontFamily || 'Roboto'
            }}
          >
            {eventData?.eventName || 'SIN NOMBRE DE EVENTO'}
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              color: settings?.headerFontColor || '#ffffff',
              mt: 1
            }}
          >
            Evento {eventData?.eventNumber} - Ronda {eventData?.round} - Heat {eventData?.heat}
          </Typography>
        </Box>

        {/* Athletes Table */}
        <Box>
          <Typography variant="h4" sx={{ mb: 2 }}>
            Atletas ({athletesList.length}):
          </Typography>
          {athletesList.map((athlete, index) => (
            <Box 
              key={index} 
              sx={{ 
                display: 'flex', 
                gap: 2, 
                mb: 2, 
                p: 2,
                backgroundColor: index % 2 === 0 ? '#333' : '#444'
              }}
            >
              <Typography sx={{ width: '50px' }}>
                #{athlete.position}
              </Typography>
              <Typography sx={{ width: '200px' }}>
                {athlete.name}
              </Typography>
              <Typography sx={{ width: '200px' }}>
                {athlete.team}
              </Typography>
              <Typography sx={{ width: '100px' }}>
                Altura: {athlete.height}
              </Typography>
              <Typography>
                Intentos: {athlete.attempts.join(' - ')}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      width: '100vw', 
      height: '100vh',
      overflow: 'hidden',
      backgroundColor: settings?.tableBackgroundColor || '#1a1a1a'
    }}>
      <VerticalEventTable
        eventData={{
        eventName:    eventData.eventName,
        eventSubtitle:eventData.eventSubtitle,
        participants: athletesList
        }}
        displayRows={displayRows}
        tableWidth={tableWidth}
        tableHeight={tableHeight}
        tableBackgroundColor={settings?.tableBackgroundColor || '#1a1a1a'}
        headerColor={settings?.headerBackgroundColor || '#2c0369'}
        positionColor={settings?.positionBackgroundColor || '#4c516d'}
        nameBackgroundColor={settings?.nameBackgroundColor || '#515e7b'}
        heightBackgroundColor={settings?.markBackgroundColor || '#151515'}
        headerFontColor={settings?.headerFontColor || '#ffffff'}
        athleteNameColor={settings?.athleteNameColor || '#ffffff'}
        teamNameColor={settings?.teamNameColor || '#e0e0e0'}
        heightColor={settings?.resultTimeColor || '#ffffff'}
        failColor={settings?.failColor || '#b71c1c'}
        passColor={settings?.passColor || '#0d47a1'}
        successColor={settings?.successColor || '#1b5e20'}
        tableRowOddColor={settings?.tableRowOddColor || '#252525'}
        tableRowEvenColor={settings?.tableRowEvenColor || '#1e1e1e'}
        headerFontSize={settings?.headerFontSize || 24}
        positionFontSize={settings?.positionFontSize || 48}
        nameFontSize={settings?.nameFontSize || 36}
        teamFontSize={settings?.teamFontSize || 24}
        heightFontSize={settings?.resultFontSize || 54}
        attemptFontSize={settings?.attemptFontSize || 32}
        showLane={settings?.showLane !== undefined ? settings.showLane : true}
        headerFontFamily={settings?.headerFontFamily || 'Roboto'}
        nameFontFamily={settings?.nameFontFamily || 'Roboto'}
        numbersFontFamily={settings?.numbersFontFamily || 'Roboto Mono'}
      />

      <SimpleVerticalDisplay 
        eventData={eventData} 
        athletesList={athletesList} 
        settings={settings} 
      />
    </Box>
  );
};

export default VerticalScoreboard;